import React, { PureComponent, Fragment } from 'react'
import { string, number, bool, arrayOf, shape } from 'prop-types'
import { Text } from 'components'
import { sortBy, flatten, filter, find, findLast } from 'lodash'
import { translate } from 'react-i18next'
import { minutes } from 'utils/string'
import platform from 'platform'
import flagStartImg from 'assets/images/flag-start.svg'
import flagFinishImg from 'assets/images/flag-finish.svg'
import { Car } from './Car'
import './RaceSimulation.css'

@translate()
export class RaceSimulation extends PureComponent {
    static SIMULATION_ANIM_DURATION = 10000 // SHOULD BE THE SAME IN RaceSimulation.css
    static SIMULATION_ANIM_DELAY = 2500 // SHOULD BE THE SAME IN RaceSimulation.css
    static SIMULATION_HIDE_TRANSITION_ANIM_DURATION = 1000 + 1000 // SHOULD BE THE SAME IN RaceSimulation.css
    static propTypes = {
        cars: arrayOf(shape({
            id: string.isRequired,
            number: number.isRequired,
            player: string.isRequired,
            position: number.isRequired,
            points: number.isRequired,
            isMe: bool.isRequired,
            segments: arrayOf(shape({
                time: number,
                pitstop: bool.isRequired,
                accumulated: number.isRequired,
                isPlayersFastLap: bool.isRequired,
                isFastLap: bool.isRequired,
                isRunning: bool.isRequired,
                hasRetiredInThisSegment: bool.isRequired
            })).isRequired
        })),
        numberOfSegments: number.isRequired,
        raceTime: number.isRequired
    }

    state = {
        isSimulating: false
    }

    componentWillMount() {
        const { cars, numberOfSegments, raceTime } = this.props

        this.cars = sortBy(cars, 'number').map(car => ({
            ...car,
            pitstopSegments: filter(car.segments, s => s.pitstop),
            playerFastLap: find(car.segments, s => s.isPlayersFastLap)
        }))

        // Gets an array of keyframes for the corresponding cars
        this.widthStepInPercentage = 100 / numberOfSegments
        this.widthPercentForSegment = index => `${Math.min(index * this.widthStepInPercentage, 100).toFixed(3)}%`
        this.timePercent = accumulated => `${((accumulated / raceTime) * 100).toFixed(3)}%`
        this.timeScaled = accumulated => `${(((accumulated / raceTime) * (RaceSimulation.SIMULATION_ANIM_DURATION / 1000)) + (RaceSimulation.SIMULATION_ANIM_DELAY / 1000)).toFixed(3)}s`

        const allKeyframeArray = cars.map(car => {
            const keyframesVectors = this.getKeyframesVectors(car, raceTime)
            return [
                this.getCarDistanceKeyframes(car, keyframesVectors),
                this.getCarColorKeyframes(car, keyframesVectors)
            ]
        })

        if (platform.name !== 'IE') {
            const styleSheet = document.styleSheets[0]
            keyFramesWithVendorPrefixArray(allKeyframeArray).forEach(keyframes => {
                styleSheet.insertRule(keyframes, styleSheet.cssRules.length)
            })
        }
    }

    componentDidMount() {
        this.setState({ isSimulating: true })
    }

    getKeyframesVectors(car, raceTime) {
        const { segments } = car
        const vectors = flatten([
            { timePercent: '0%', initial: true },
            ...filter(segments, s => s.isRunning || s.hasRetiredInThisSegment).map(segment => {
                if (segment.hasRetiredInThisSegment) {
                    const timePercent = (segment.accumulated * 100) / raceTime
                    const timePercentPlusABitMore = `${Math.min(timePercent + 3, 100).toFixed(3)}%`
                    return [
                        {
                            timePercent: timePercentPlusABitMore,
                            segment,
                            isOut: true
                        },
                        {
                            timePercent: '100%',
                            isOut: true,
                            final: true
                        }
                    ]
                }
                return {
                    timePercent: this.timePercent(segment.accumulated),
                    segment
                }
            })
        ])
        return [...vectors, { timePercent: '100%', isOut: vectors[vectors.length - 1].isOut, final: true }]
    }

    getCarDistanceKeyframes(car, keyframesVectors) {
        const keyframes = keyframesVectors.map(({ timePercent, isOut, final }, index) => {
            if (isOut) {
                return `${timePercent} {width:${this.widthPercentForSegment(final ? index - 2 : index - 1)}; filter:grayscale(100%) opacity(30%)}`
            }
            return `${timePercent} {width:${this.widthPercentForSegment(index)}; filter:grayscale(0%) opacity(100%)}`
        })
        return `@keyframes car-anim-id-${car.id} {${keyframes.join('\n')}}`
    }
    getCarColorKeyframes(car, keyframesVectors) {
        const keyframes = keyframesVectors.map(({ timePercent, segment = {}, isOut, final }) => {
            const lastSegment = final ? findLast(keyframesVectors, v => !v.isOut && !v.final).segment : segment
            return `${timePercent} {fill:${rankingToColor(isOut ? null : lastSegment.rankingAccumulated)}}`
        })
        return `@keyframes car-anim-color-id-${car.id} {${keyframes.join('\n')}}`
    }

    renderCarLane = ({ id, color, player, isMe, time, accumulatedTime, position, points, pitstopSegments, playerFastLap }) => {
        const { isSimulating } = this.state
        const animationDelay = this.timeScaled(accumulatedTime)
        return (
            <div key={id} className={['RaceSimulation-CarLane flex row-stretch', { IsMe: isMe }]}>
                <Text
                    className="RaceSimulation-Position"
                    style={{ animationDelay }}
                >
                    {position}
                </Text>
                <div className="RaceSimulation-Lane flex-grow">
                    <div className="w-100 h-100 position-relative">
                        <div className="RaceSimulation-LaneProgress" style={isSimulating ? { animationName: `car-anim-id-${id}` } : null}>
                            <svg preserveAspectRatio="none" viewBox="0 0 1 1">
                                <defs>
                                    <linearGradient id="fadeGrad" x1="0" y1="0" x2="1" y2="0">
                                        <stop offset="0" stopColor="white" stopOpacity="0" />
                                        <stop offset="0.5" stopColor="white" stopOpacity="0.6" />
                                        <stop offset="0.95" stopColor="white" stopOpacity="0.5" />
                                        <stop offset="1" stopColor="white" stopOpacity="0" />
                                    </linearGradient>
                                    <mask id="fade" maskContentUnits="objectBoundingBox">
                                        <rect x="0" y="0" width="1" height="1" fill="url(#fadeGrad)" />
                                    </mask>
                                </defs>
                                <rect x="0" y="0" width="1" height="1" mask="url(#fade)" style={isSimulating ? { animationName: `car-anim-color-id-${id}` } : null} />
                            </svg>
                            <Text className="nickname">{player}</Text>
                        </div>
                        {pitstopSegments.map(this.renderPitstop)}
                        {/* playerFastLap && this.renderFastLap(playerFastLap) */}
                        {time && (
                            <div
                                className="RaceSimulation-Time"
                                style={{ animationDelay }}
                            >
                                <Text>{minutes(time)}</Text>
                            </div>
                        )}
                        <div className="RaceSimulation-CarProgress" style={isSimulating ? { animationName: `car-anim-id-${id}` } : null}>
                            <Car color={color} />
                        </div>
                    </div>
                </div>
                <Text
                    className="RaceSimulation-Points"
                    style={{ animationDelay }}
                >
                    {points}
                </Text>
            </div>
        )
    }

    renderPitstop = ({ lap, accumulated }) => (
        <div
            key={lap}
            className="RaceSimulation-Pitstop"
            style={{
                left: this.widthPercentForSegment(lap - 1),
                animationDelay: this.timeScaled(accumulated)
            }}
        />
    )
    renderFastLap = ({ lap, time, accumulated, isFastLap }) => (
        <div
            className={['RaceSimulation-FastLap', { FastLap: isFastLap }]}
            style={{
                left: this.widthPercentForSegment(lap - 1),
                animationDelay: this.timeScaled(accumulated)
            }}
        >
            <Text>{isFastLap ? 'Fastlap : ' : ''}{minutes(time)}</Text>
        </div>
    )

    render() {
        const { t } = this.props
        const { isSimulating } = this.state
        return (
            <Fragment>
                <div className="RaceSimulation-SimulationBackground darkmode-background" />
                <div className={['RaceSimulation h-100 flex col-stretch', { Simulating: isSimulating }]}>
                    <div className="RaceSimulation-Header flex row-stretch">
                        <div className="RaceSimulation-Position">
                            <img src={flagStartImg} alt={t('game.RACE.position')} />
                            <Text>{t('game.RACE.position')}</Text>
                        </div>
                        <div className="flex-spacer" />
                        <div className="RaceSimulation-Legend flex row-center">
                            <div className="RaceSimulation-Legend-Item Pitstop">
                                {t('game.RACE.legend.pitstop')}
                            </div>
                        </div>
                        <div className="flex-spacer" />
                        <div className="RaceSimulation-Points">
                            <img src={flagFinishImg} alt={t('game.RACE.points')} />
                            <Text>{t('game.RACE.points')}</Text>
                        </div>
                    </div>
                    <div className="flex-grow OverflowHidden">
                        <div className="Scrollable">
                            {this.cars.map(this.renderCarLane)}
                        </div>
                    </div>
                </div>
            </Fragment>
        )
    }
}

// function shuffleArray(array) {
//     const suffled = [...array]
//     for (let i = suffled.length - 1; i > 0; i--) {
//         const j = Math.floor(Math.random() * (i + 1));
//         [suffled[i], suffled[j]] = [suffled[j], suffled[i]] // eslint-disable-line no-param-reassign
//     }
//     return suffled
// }

function keyFramesWithVendorPrefixArray(keyframes) {
    return flatten(flatten(keyframes).map(kfs => [kfs, kfs.replace('@keyframes', '@-webkit-keyframes')]))
}

// ranking is a value from 0 to 1 that represent how the player has done (in time) in the current lap respect the fastest and slowest one
const colorForFastest = [164, 235, 217, 1]
const colorForSlowest = [235, 162, 172, 0.3]
function rankingToColor(ranking) {
    if (typeof ranking !== 'number') return 'rgba(100,100,100,0.5)'
    const [r, g, b, o] = rankingToRGB(ranking)
    return `rgba(${r},${g},${b},${o})`
}
function rankingToRGB(ranking) {
    return [
        Math.round(colorInterpolate(ranking, colorForFastest[0], colorForSlowest[0])),
        Math.round(colorInterpolate(ranking, colorForFastest[1], colorForSlowest[1])),
        Math.round(colorInterpolate(ranking, colorForFastest[2], colorForSlowest[2])),
        colorInterpolate(ranking, colorForFastest[3], colorForSlowest[3])
    ]
}
function colorInterpolate(v, c1, c2) {
    return c2 + (v * (c1 - c2))
}




// WEBPACK FOOTER //
// src/components/RaceSimulation/RaceSimulation.js